import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../components/Toast';
import { auth, db } from '../firebase';
import { collection, doc, getCountFromServer, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import {
  formatPlanName,
  getPlanColor,
  getLimitsForPlan,
  calculateUsagePercentages,
  addonMetadata,
  normalizeAddons,
  planLimits,
  formatearUso
} from '../utils/planHelpers';
import { useOrganization } from '../context/OrganizationContext';
import PageHeader from '../components/PageHeader';
import { useTranslation } from 'react-i18next';
import AddPaymentMethodModal from '../components/billing/AddPaymentMethodModal';
import CancelAddonModal from '../components/billing/CancelAddonModal';
import UpgradeConfirmModal from '../components/billing/UpgradeConfirmModal';
import DowngradeConfirmModal from '../components/billing/DowngradeConfirmModal';
import CancelSubscriptionModal from '../components/billing/CancelSubscriptionModal';
import useUpgradeToPro from '../hooks/useUpgradeToPro';
import useDowngradeToStarter from '../hooks/useDowngradeToStarter';
import useCancelSubscription from '../hooks/useCancelSubscription';
import './Billing.css';

import { esCuentaInterna } from '../utils/estadoDeFacturacion';

/**
 * UNA TARJETA QUE NINGÚN PLAN INCLUYE NO SE PINTA (8-oct-2026).
 *
 * Pintarla con la cruz de «no incluido» anuncia una función que NO EXISTE y le dice al
 * cliente que podría comprarla. Dos casos hoy:
 *
 *   · **Email** — no hay flujo de conexión en `functions/integrations/`; vuelve en la etapa 2
 *     de correos, vía Resend.
 *   · **Audio TTS** — el bot nunca ha enviado un audio (cero salientes de tipo audio en todo
 *     el histórico); `synthesizeAudio` se retiró por muerta el 7-oct.
 *
 * Se DERIVA del catálogo; no es un interruptor. El día que la función vuelva basta con poner
 * su flag en `true` y la tarjeta reaparece sola. Un booleano a mano aquí sería una cosa más
 * que alguien tendría que acordarse de mover, y este repo ya tiene el caso del campo que
 * nadie volvió a mirar.
 */
const enAlgunPlan = (grupo, clave) => Object.values(planLimits)
  .some((plan) => plan && plan[grupo] && plan[grupo][clave] === true);

const EMAIL_EN_ALGUN_PLAN = enAlgunPlan('channelsIncluded', 'email');
const TTS_EN_ALGUN_PLAN = enAlgunPlan('featuresIncluded', 'audioTTS');
const API_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || "https://us-central1-sistema-bks.cloudfunctions.net";

export default function Billing() {
  const [cliente, setCliente] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  // Cantidad REAL de documentos en `numeros`. `null` = todavía sin contar: la tarjeta no se
  // pinta. Sustituye a `usage.numerosCount`, que valía −2 en producción con un número real.
  const [numerosReales, setNumerosReales] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [cancelModalAddon, setCancelModalAddon] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [showCancelSubscriptionModal, setShowCancelSubscriptionModal] = useState(false);
  const [confirmingRemoveId, setConfirmingRemoveId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const { context, loading: contextLoading } = useOrganization();
  const { t, i18n } = useTranslation('billing');
  const { t: tc } = useTranslation('common');

  // Gate de billing por rol: en modo personal el usuario ES el dueño; en modo
  // organización solo owner o quien tenga canManageBilling. Envuelve SOLO las
  // ACCIONES (upgrade/downgrade/cancelar/tarjetas/addons) — el estado de plan,
  // uso, facturas, el listener onSnapshot y el dunning banner se muestran a
  // cualquier miembro (lectura). El backend ya exige canManageBilling
  // (assertOrgBillingAccess); esto es el gate de UI equivalente.
  const canManageBilling =
    context.mode !== 'organization' ||
    context.member?.role === 'owner' ||
    context.member?.permissions?.canManageBilling === true;

  // onSynced para los hooks: el doc `cliente` ya es reactivo vía onSnapshot, así
  // que tras un cambio de plan/suscripción solo hay que re-fetchear lo que el
  // listener NO cubre (invoices/paymentMethods de Stripe). Reemplaza el
  // window.location.reload() de los hooks — sin recarga de página en /billing.
  const refetchStripeData = async () => {
    const u = auth.currentUser;
    if (!u) return;
    // Misma puerta que en el listener: una cuenta interna no tiene nada que refrescar en Stripe.
    if (esCuentaInterna(cliente?.billing)) return;
    const idToken = await u.getIdToken();
    const orgParam = context.mode === 'organization' && context.orgId
      ? `&organizationId=${context.orgId}` : '';
    await Promise.all([
      loadInvoices(u.uid, orgParam, idToken),
      loadPaymentMethods(u.uid, orgParam, idToken)
    ]);
  };

  const { prorrateo, loadingPreview, processing: upgradeProcessing, calcularPreview, confirmarUpgrade } = useUpgradeToPro(context, { onSynced: refetchStripeData });
  const {
    prorrateo: downgradePreview,
    blocked: downgradeBlocked,
    loadingPreview: loadingDowngradePreview,
    processing: downgradeProcessing,
    calcularPreview: calcularDowngradePreview,
    confirmarDowngrade,
  } = useDowngradeToStarter(context, { onSynced: refetchStripeData });
  const {
    processing: cancelSubscriptionProcessing,
    cancelarSuscripcion,
    mantenerSuscripcion,
  } = useCancelSubscription(context, { onSynced: refetchStripeData });

  const handleOpenUpgradeModal = () => {
    if (!canManageBilling) return;
    setShowUpgradeModal(true);
    calcularPreview();
  };

  const handleOpenDowngradeModal = () => {
    if (!canManageBilling) return;
    setShowDowngradeModal(true);
    calcularDowngradePreview();
  };

  // Load client data and billing information.
  // `cliente` (doc de Firestore) se escucha con onSnapshot: refleja en vivo
  // cualquier cambio del doc — webhook, otra pestaña, o una acción rechazada por
  // un guard que ya cambió el estado — sin recarga manual. La página SOLO LEE
  // este doc; nunca lo escribe (las mutaciones van por endpoints server-side),
  // así que no hay ciclo listener→write. Los datos de Stripe (invoices/pms) se
  // cargan una sola vez acá (no en el callback del snapshot) y se refrescan vía
  // refetchStripeData tras un cambio de plan/suscripción.
  useEffect(() => {
    if (contextLoading) return;

    let unsubSnapshot = null;

    const unsubAuth = onAuthStateChanged(auth, (u) => {
      if (!u) {
        navigate('/');
        return;
      }

      setUser(u);

      // Org-aware: read from organization or personal document
      const docRef = context.mode === 'organization' && context.orgId
        ? doc(db, 'organizations', context.orgId)
        : doc(db, 'clientes', u.uid);

      // Limpia un listener previo si onAuthStateChanged vuelve a disparar.
      if (unsubSnapshot) unsubSnapshot();

      let stripeLoaded = false;
      let contado = false;
      unsubSnapshot = onSnapshot(docRef, async (snap) => {
        if (!snap.exists()) {
          console.log('Usuario sin plan, redirigiendo a /contratar');
          navigate('/contratar');
          return;
        }

        const datos = snap.data();
        setCliente({ id: snap.id, ...datos });

        // NÚMEROS · se cuentan los DOCUMENTOS, una sola vez, con una agregación del servidor:
        // devuelve la cifra sin traerse los documentos (mismo mecanismo que `ChatsExport.jsx:67`).
        // Esta tarjeta leía `usage.numerosCount`, que estaba en −2 con un número conectado.
        // Si contar falla no se pinta la tarjeta y la página sigue: un número de adorno sería
        // peor que un hueco.
        if (!contado) {
          contado = true;
          const refNumeros = context.mode === 'organization' && context.orgId
            ? collection(db, 'organizations', context.orgId, 'numeros')
            : collection(db, 'clientes', u.uid, 'numeros');
          getCountFromServer(refNumeros)
            .then((agg) => setNumerosReales(agg.data().count))
            .catch((error) => console.warn('No se pudo contar los números:', error?.message));
        }

        // Datos de Stripe: una sola vez (guardado con un flag para no re-pegarle
        // a Stripe en cada cambio del doc).
        //
        // UNA CUENTA INTERNA NO LOS PIDE. No tiene suscripción ni cliente en Stripe, así que
        // las dos llamadas devolvían 500 y la pantalla quedaba con las secciones vacías sin
        // explicar por qué. Medido el 8-oct-2026 en la org de PUX: cuatro `getInvoiceHistory`
        // y cuatro `listPaymentMethods` con `No such customer` en 24 h, uno por visita.
        if (!stripeLoaded && !esCuentaInterna(datos.billing)) {
          stripeLoaded = true;
          try {
            const orgParam = context.mode === 'organization' && context.orgId
              ? `&organizationId=${context.orgId}` : '';
            const idToken = await u.getIdToken();
            await Promise.all([
              loadInvoices(u.uid, orgParam, idToken),
              loadPaymentMethods(u.uid, orgParam, idToken)
            ]);
          } catch (error) {
            console.error('Error loading Stripe billing data:', error);
          }
        }

        setLoading(false);
      }, (error) => {
        console.error('Error loading billing data:', error);
        setLoading(false);
      });
    });

    return () => {
      unsubAuth && unsubAuth();
      unsubSnapshot && unsubSnapshot();
    };
  }, [navigate, contextLoading, context.mode, context.orgId]);

  const loadInvoices = async (uid, orgParam = '', idToken) => {
    try {
      const response = await fetch(`${API_URL}/getInvoiceHistory?uid=${uid}&limit=12${orgParam}`, {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      const data = await response.json();

      if (data.invoices) {
        setInvoices(data.invoices.map(inv => ({
          id: inv.id,
          date: new Date(inv.date),
          amount: inv.amount,
          currency: inv.currency,
          status: inv.status,
          plan: inv.description || 'pro',
          pdfUrl: inv.invoicePdf || inv.hostedInvoiceUrl || '#'
        })));
      } else {
        setInvoices([]);
      }
    } catch (error) {
      console.error('Error loading invoices:', error);
      setInvoices([]);
    }
  };

  const loadPaymentMethods = async (uid, orgParam = '', idToken) => {
    try {
      const response = await fetch(`${API_URL}/listPaymentMethods?uid=${uid}${orgParam}`, {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      const data = await response.json();
      setPaymentMethods(data.paymentMethods || []);
    } catch (error) {
      console.error('Error loading payment methods:', error);
      setPaymentMethods([]);
    }
  };

  const handleAddPaymentMethod = () => {
    if (!canManageBilling) return;
    setShowAddPaymentModal(true);
  };

  const handlePaymentMethodAdded = async () => {
    if (user) {
      const orgParam = context.mode === 'organization' && context.orgId
        ? `&organizationId=${context.orgId}` : '';
      const idToken = await user.getIdToken();
      await loadPaymentMethods(user.uid, orgParam, idToken);
    }
  };

  const handleRemovePaymentMethod = async (pmId) => {
    if (!user || !canManageBilling) return;
    setRemovingId(pmId);
    try {
      const idToken = await user.getIdToken();
      const orgBody = context.mode === 'organization' && context.orgId
        ? { organizationId: context.orgId } : {};
      const resp = await fetch(`${API_URL}/removePaymentMethod`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ ...orgBody, paymentMethodId: pmId })
      });
      const data = await resp.json();
      // El backend devuelve 409 LAST_CARD con un mensaje claro (guard de última
      // tarjeta con suscripción activa); se muestra tal cual.
      if (!resp.ok) throw new Error(data.error || t('paymentMethods.removeError'));

      setConfirmingRemoveId(null);
      const orgParam = context.mode === 'organization' && context.orgId
        ? `&organizationId=${context.orgId}` : '';
      await loadPaymentMethods(user.uid, orgParam, idToken);
    } catch (err) {
      toast.error(err.message || t('paymentMethods.removeError'));
    } finally {
      setRemovingId(null);
    }
  };

  const handleDownloadInvoice = (invoice) => {
    if (invoice.pdfUrl && invoice.pdfUrl !== '#') {
      window.open(invoice.pdfUrl, '_blank');
    } else {
      toast.info(t('invoiceNoPdf'));
    }
  };

  // Cancel addon at end of billing period
  const handleCancelAddon = async (addonId) => {
    if (!user || !canManageBilling) return;
    const payload = { addons: [addonId] };
    if (context.mode === 'organization' && context.orgId) {
      payload.organizationId = context.orgId;
    }
    const idToken = await user.getIdToken();

    const response = await fetch(`${API_URL}/cancelAddon`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || t('errorCancel'));

    // Update local state
    setCliente(prev => ({
      ...prev,
      addonsPendingCancel: {
        ...(prev.addonsPendingCancel || {}),
        [addonId]: { cancelAt: { seconds: new Date(data.cancelAt).getTime() / 1000 }, quantity: 1 }
      }
    }));
  };

  // Undo pending cancellation
  const handleKeepAddon = async (addonId) => {
    if (!user || !canManageBilling) return;
    const payload = { addons: [addonId], undo: true };
    if (context.mode === 'organization' && context.orgId) {
      payload.organizationId = context.orgId;
    }
    const idToken = await user.getIdToken();

    const response = await fetch(`${API_URL}/cancelAddon`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const data = await response.json();
      toast.error(data.error || t('errorRestoreAddon'));
      return;
    }

    // Update local state
    setCliente(prev => {
      const pending = { ...(prev.addonsPendingCancel || {}) };
      delete pending[addonId];
      return { ...prev, addonsPendingCancel: pending };
    });
  };

  if (loading) {
    return <div className="billing-loading">{t('loadingBilling')}</div>;
  }

  if (!cliente) return null;

  const planId = cliente.planId || cliente.plan || 'starter';
  const planColor = getPlanColor(planId);
  const planConfig = getLimitsForPlan(planId);
  const usage = calculateUsagePercentages(cliente, numerosReales);
  const limits = cliente.limits || planConfig;

  const addonsActivosRaw = cliente.addonsActivos || {};
  const addonsActivos = normalizeAddons(addonsActivosRaw);
  const hasAddonActive = (id) => (addonsActivos[id] || 0) > 0;
  const addonsPendingCancel = cliente.addonsPendingCancel || {};

  // Real billing date from Firestore
  const billingTimestamp = cliente.billing?.currentPeriodEnd || cliente.finalizaPeriodoEn;
  const nextBillingDate = billingTimestamp
    ? new Date(billingTimestamp.seconds * 1000)
    : null;

  // Real price from plan config (USD)
  const planPrice = planConfig.price || 0;

  // billing.cancelAtPeriodEnd solo existe en docs de organización (fix de
  // ayer) — todo signup nuevo desde /contratar termina en modo org, así que
  // cubre el camino real. Modo personal (legado, sin cuentas activas hoy)
  // tiene un bug conocido y no arreglado esta sesión (finalizaPeriodoEn
  // queda pegado tras reactivar en stripeWebhook.js) — para esas cuentas el
  // badge de "pendiente de cancelación" simplemente no se muestra; el botón
  // de cancelar/backend siguen funcionando igual.
  const cancelAtPeriodEnd = !!cliente.billing?.cancelAtPeriodEnd;
  // Cuenta interna de PUX (billing.interna): acceso sin cobro y sin suscripción en Stripe.
  const cuentaInterna = esCuentaInterna(cliente.billing);
  const hasActiveSubscription = !!(cliente.billing?.subscriptionId || cliente.subscriptionId)
    && cliente.billing?.status !== 'canceled'
    && cliente.estadoPago !== 'cancelado';

  // Get cancel date for modal
  const getCancelDate = () => {
    if (nextBillingDate) return nextBillingDate.toISOString();
    return null;
  };

  // Active addons list for display
  const activeAddonsList = Object.entries(addonsActivos)
    .filter(([, qty]) => qty > 0)
    .map(([id, qty]) => ({
      id,
      quantity: qty,
      metadata: addonMetadata[id] || { name: id, icon: '?', pricePerUnit: 0 },
      pendingCancel: addonsPendingCancel[id] || null
    }));

  return (
    <div className="billing-container">
      {user && (
        <AddPaymentMethodModal
          user={user}
          organizationId={context.mode === 'organization' && context.orgId ? context.orgId : undefined}
          isOpen={showAddPaymentModal}
          onClose={() => setShowAddPaymentModal(false)}
          onSuccess={handlePaymentMethodAdded}
        />
      )}

      <CancelAddonModal
        isOpen={!!cancelModalAddon}
        onClose={() => setCancelModalAddon(null)}
        addon={cancelModalAddon}
        cancelAt={getCancelDate()}
        onConfirm={handleCancelAddon}
      />

      <UpgradeConfirmModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        prorrateo={prorrateo}
        loadingPreview={loadingPreview}
        processing={upgradeProcessing}
        onConfirm={confirmarUpgrade}
      />

      <DowngradeConfirmModal
        isOpen={showDowngradeModal}
        onClose={() => setShowDowngradeModal(false)}
        prorrateo={downgradePreview}
        blocked={downgradeBlocked}
        loadingPreview={loadingDowngradePreview}
        processing={downgradeProcessing}
        onConfirm={confirmarDowngrade}
      />

      <CancelSubscriptionModal
        isOpen={showCancelSubscriptionModal}
        onClose={() => setShowCancelSubscriptionModal(false)}
        planName={formatPlanName(planId)}
        cancelAt={getCancelDate()}
        processing={cancelSubscriptionProcessing}
        onConfirm={cancelarSuscripcion}
      />

      {/* Header */}
      <PageHeader title={t('title')} subtitle={t('subtitle')} />

      {/* Current Plan */}
      <section className="current-plan-section">
        <div className="plan-card">
          <div className="plan-header">
            <div className="plan-info">
              <h2>{formatPlanName(planId)}</h2>
              <p className="plan-price">
                {planPrice > 0 ? t('plan.pricePerMonth', { price: planPrice }) : t('plan.free')}
              </p>
            </div>
            <div className="plan-badge-large" style={{ backgroundColor: planColor }}>
              {planId === 'pro' ? 'PRO' : 'STARTER'}
            </div>
          </div>

          <div className="plan-details">
            <div className="detail-item">
              <span className="detail-label">{t('plan.nextBilling')}</span>
              <span className="detail-value">
                {nextBillingDate
                  ? nextBillingDate.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
                  : t('plan.notAvailable')}
              </span>
            </div>
            {cuentaInterna && (
              <div className="detail-item cuenta-interna">
                <span className="detail-label">{t('plan.internalAccount')}</span>
                <span className="detail-value">{t('plan.internalAccountDesc')}</span>
              </div>
            )}

            {canManageBilling && !cuentaInterna && planId === 'starter' && !cancelAtPeriodEnd && (
              <button className="upgrade-btn-large" onClick={handleOpenUpgradeModal}>
                {t('plan.upgradeToPro')}
              </button>
            )}

            {canManageBilling && !cuentaInterna && planId === 'pro' && !cancelAtPeriodEnd && (
              <button className="downgrade-btn-large" onClick={handleOpenDowngradeModal}>
                {t('plan.downgradeToStarter')}
              </button>
            )}

            {cancelAtPeriodEnd ? (
              <div className="subscription-cancel-pending">
                {/* Badge de estado (info) visible a todos; botón keep sólo gestores. */}
                <span className="addon-pending-badge">
                  {t('addons.cancelsOn', {
                    date: nextBillingDate
                      ? nextBillingDate.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
                      : t('cancelModal.endOfPeriod')
                  })}
                </span>
                {canManageBilling && (
                  <button
                    className="addon-keep-btn"
                    onClick={mantenerSuscripcion}
                    disabled={cancelSubscriptionProcessing}
                  >
                    {t('addons.keep')}
                  </button>
                )}
              </div>
            ) : (
              canManageBilling && !cuentaInterna && hasActiveSubscription && (
                <button
                  className="cancel-subscription-link"
                  onClick={() => setShowCancelSubscriptionModal(true)}
                >
                  {t('plan.cancelSubscription')}
                </button>
              )
            )}
          </div>
        </div>
      </section>

      {/* Features Included */}
      <section className="features-included-section">
        <h2 className="section-title">{t('features.title')}</h2>
        <div className="features-grid">
          <FeatureCard icon="📱" name="WhatsApp" included={limits.channelsIncluded?.whatsapp !== false} />
          <FeatureCard icon="📸" name="Instagram" included={limits.channelsIncluded?.instagram !== false} />
          <FeatureCard icon="🎵" name="TikTok" included={limits.channelsIncluded?.tiktok !== false} />
          <FeatureCard icon="💬" name="Messenger" included={limits.channelsIncluded?.messenger !== false} />
          {EMAIL_EN_ALGUN_PLAN && (
            <FeatureCard icon="📧" name="Email" included={limits.channelsIncluded?.email || false} />
          )}
          <FeatureCard icon="📅" name="Calendar" included={limits.featuresIncluded?.calendar || hasAddonActive('addon_calendar')} />
          <FeatureCard icon="🛒" name="E-Commerce" included={limits.featuresIncluded?.ecommerce || hasAddonActive('addon_ecommerce')} />
          <FeatureCard icon="🎤" name="Audio STT" included={limits.featuresIncluded?.audioSTT !== false} />
          {TTS_EN_ALGUN_PLAN && (
            <FeatureCard icon="🔊" name="Audio TTS" included={limits.featuresIncluded?.audioTTS !== false} />
          )}
          <FeatureCard icon="📄" name="SmartDocs" included={limits.featuresIncluded?.repositorios || hasAddonActive('addon_repos')} />
        </div>
      </section>

      {/* Usage Stats - Complete */}
      <section className="usage-stats-section">
        <h2 className="section-title">{t('usage.title')}</h2>
        <div className="usage-grid">
          {/* Bots */}
          <UsageCard label={t('usage.bots')} usage={usage.bots} planColor={planColor} idioma={i18n.language} t={t} />

          {/* Mensajes. Dejaron de ser informativos el 4-sep-2026: son la cuota
              que PAUSA el asistente al 100 %, así que necesitan su barra como
              las demás. El comentario que había aquí —«informative only, no
              limit bar»— era cierto cuando se escribió y dejó de serlo sin que
              nadie volviera a mirarlo. */}
          <UsageCard label={t('usage.messages')} usage={usage.messages} planColor={planColor} idioma={i18n.language} t={t}
            avisoLimite="usage.messagesLimitReached" />

          {/* Images */}
          <UsageCard label={t('usage.images')} usage={usage.images} planColor={planColor} idioma={i18n.language} t={t} />

          {/* WhatsApp Numbers */}
          <UsageCard label={t('usage.whatsappNumbers')} usage={usage.numeros} planColor={planColor} idioma={i18n.language} t={t} />

          {/* Seats - only show if plan allows more than 1 */}
          {usage.seats && usage.seats.total > 1 && (
            <UsageCard label={t('usage.teamMembers')} usage={usage.seats} planColor={planColor} idioma={i18n.language} t={t} />
          )}

          {/* Audio Minutes */}
          {usage.audioMinutes && usage.audioMinutes.total > 0 && (
            <UsageCard label={t('usage.audioMinutes')} usage={usage.audioMinutes} planColor={planColor} unit="min" idioma={i18n.language} t={t} />
          )}

          {/* Procesamiento mensual: corta desde F2, y es distinto del
              almacenamiento — guardar cuesta $0,44/GB-mes y procesar $1,90/GB
              una vez, así que borrar un documento devuelve espacio pero NO
              devuelve procesamiento. Por eso son dos medidores y no uno. */}
          <UsageCard label={t('usage.procesamiento')} usage={usage.procesamiento} planColor={planColor} unit="MB" idioma={i18n.language} t={t} />

          {/* SmartDocs Storage */}
          {usage.repos && usage.repos.total > 0 && (
            <UsageCard label={t('usage.smartDocs')} usage={usage.repos} planColor={planColor} unit="MB" idioma={i18n.language} t={t} />
          )}
        </div>
      </section>

      {/* Active Addons */}
      <section className="addons-section">
        <div className="section-header-with-action">
          <h2 className="section-title">{t('addons.title')}</h2>
          {canManageBilling && (
            <button className="add-addons-btn" onClick={() => navigate('/addon')}>
              {t('addons.add')}
            </button>
          )}
        </div>

        {activeAddonsList.length === 0 ? (
          <div className="empty-state">
            <p>{t('addons.empty')}</p>
            {canManageBilling && (
              <button className="add-addons-btn" onClick={() => navigate('/addon')}>
                {t('addons.explore')}
              </button>
            )}
          </div>
        ) : (
          <div className="addons-grid">
            {activeAddonsList.map(({ id, quantity, metadata: meta, pendingCancel }) => (
              <div key={id} className="addon-active-card">
                <span className="addon-icon">{meta.icon}</span>
                <div className="addon-info">
                  <h4 className="addon-name">
                    {meta.name}{quantity > 1 ? ` x${quantity}` : ''}
                  </h4>
                  <p className="addon-price">
                    ${meta.pricePerUnit}{quantity > 1 ? ` x ${quantity} = $${meta.pricePerUnit * quantity}` : ''}/mes
                  </p>
                </div>
                {pendingCancel ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
                    <span className="addon-pending-badge">
                      {t('addons.cancelsOn', { date: new Date(pendingCancel.cancelAt.seconds * 1000).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) })}
                    </span>
                    {canManageBilling && (
                      <button className="addon-keep-btn" onClick={() => handleKeepAddon(id)}>
                        {t('addons.keep')}
                      </button>
                    )}
                  </div>
                ) : (
                  canManageBilling && (
                    <button
                      className="addon-cancel-btn"
                      onClick={() => setCancelModalAddon({ id, name: meta.name })}
                    >
                      {t('addons.cancelAddon')}
                    </button>
                  )
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Payment Methods */}
      <section className="payment-methods-section">
        <div className="section-header-with-action">
          <h2 className="section-title">{t('paymentMethods.title')}</h2>
          {canManageBilling && !cuentaInterna && (
            <button className="add-payment-btn" onClick={handleAddPaymentMethod}>
              {t('paymentMethods.add')}
            </button>
          )}
        </div>

        {/* Una cuenta interna no tiene cliente en Stripe: ni se le piden los datos ni se le
            ofrece añadir una tarjeta. Sin esto, la sección quedaba vacía sin decir por qué. */}
        <div className="payment-methods-list">
          {cuentaInterna ? (
            <div className="empty-state">
              <p>{t('paymentMethods.noAplicaInterna')}</p>
            </div>
          ) : paymentMethods.length === 0 ? (
            <div className="empty-state">
              <p>{t('paymentMethods.empty')}</p>
              {canManageBilling && (
                <button className="add-payment-btn-large" onClick={handleAddPaymentMethod}>
                  {t('paymentMethods.addFull')}
                </button>
              )}
            </div>
          ) : (
            paymentMethods.map(method => (
              <div key={method.id} className="payment-method-card">
                <div className="payment-method-icon">💳</div>
                <div className="payment-method-info">
                  <h4 className="payment-method-brand">
                    {method.brand.toUpperCase()} •••• {method.last4}
                  </h4>
                  <p className="payment-method-expiry">
                    {t('paymentMethods.expires', { month: method.expMonth, year: method.expYear })}
                  </p>
                </div>
                {method.isDefault && (
                  <span className="default-badge">{t('paymentMethods.default')}</span>
                )}
                {canManageBilling && (confirmingRemoveId === method.id ? (
                  <div className="remove-confirm-inline">
                    <span>{t('paymentMethods.removeConfirm')}</span>
                    <button
                      className="remove-confirm-yes"
                      onClick={() => handleRemovePaymentMethod(method.id)}
                      disabled={removingId === method.id}
                    >
                      {removingId === method.id ? tc('processing') : t('paymentMethods.removeYes')}
                    </button>
                    <button
                      className="remove-confirm-no"
                      onClick={() => setConfirmingRemoveId(null)}
                      disabled={removingId === method.id}
                    >
                      {t('paymentMethods.removeNo')}
                    </button>
                  </div>
                ) : (
                  <button
                    className="remove-payment-btn"
                    onClick={() => setConfirmingRemoveId(method.id)}
                  >
                    {t('paymentMethods.remove')}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      </section>

      {/* Invoice History */}
      <section className="invoice-history-section">
        <h2 className="section-title">{t('invoices.title')}</h2>

        {cuentaInterna ? (
          <div className="empty-state">
            <p>{t('invoices.noAplicaInterna')}</p>
          </div>
        ) : invoices.length === 0 ? (
          <div className="empty-state">
            <p>{t('invoices.empty')}</p>
          </div>
        ) : (
          <div className="invoices-table">
            <div className="invoice-row invoice-header-row">
              <div className="invoice-cell">{t('invoices.date')}</div>
              <div className="invoice-cell">{t('invoices.plan')}</div>
              <div className="invoice-cell">{t('invoices.amount')}</div>
              <div className="invoice-cell">{t('invoices.status')}</div>
              <div className="invoice-cell">{t('invoices.action')}</div>
            </div>

            {invoices.map(invoice => (
              <div key={invoice.id} className="invoice-row">
                <div className="invoice-cell">
                  {invoice.date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
                <div className="invoice-cell">{formatPlanName(invoice.plan)}</div>
                <div className="invoice-cell">
                  ${invoice.amount.toLocaleString()} {invoice.currency}
                </div>
                <div className="invoice-cell">
                  <span className={`status-badge ${invoice.status}`}>
                    {invoice.status === 'paid' ? t('invoices.paid') : t('invoices.pending')}
                  </span>
                </div>
                <div className="invoice-cell">
                  <button className="download-btn" onClick={() => handleDownloadInvoice(invoice)}>
                    {t('invoices.download')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * `avisoLimite`: qué decir cuando la barra llega al 100 %.
 *
 * Por defecto `usage.limitReached` —«Límite alcanzado»—, que basta para las
 * cuotas cuyo tope sólo impide AÑADIR. Los mensajes necesitan otra cosa: ahí el
 * tope PAUSA el asistente, y lo que evita la sorpresa no es el número sino
 * saber que los mensajes siguen llegando a la bandeja.
 */
function UsageCard({ label, usage, planColor, formatUsed, unit, idioma, t, avisoLimite }) {
  if (!usage) return null;
  // Sin formato por defecto, un contador con decimales se pintaba crudo —`repoStorageMB` valía
  // 5.999969482421875— y el único que lo formateaba lo hacía con `toFixed(0)`, que mostraba «0»
  // para 0,1285 minutos de audio reales. `formatearUso` cubre los dos extremos (8-oct-2026).
  const displayUsed = formatUsed ? formatUsed(usage.used) : formatearUso(usage.used, unit, idioma);
  // El TOTAL también pasa por el formato: «72000» se leía sin miles. La unidad va en null a
  // propósito — un total de minutos jamás se convierte a segundos, eso es solo para lo usado.
  const totalFormateado = formatearUso(usage.total, null, idioma);
  const displayTotal = unit ? `${totalFormateado} ${unit}` : totalFormateado;

  return (
    <div className="usage-card">
      <div className="usage-header">
        <span className="usage-label">{label}</span>
        <span className="usage-value">
          {displayUsed} / {displayTotal}
        </span>
      </div>
      <div className="progress-bar">
        <div
          className="progress-fill"
          style={{
            width: `${Math.min(usage.percentage, 100)}%`,
            backgroundColor: usage.percentage >= 100 ? 'var(--ios-red)' : planColor
          }}
        ></div>
      </div>
      {usage.percentage >= 80 && (
        <p className="usage-warning">
          {usage.percentage >= 100
            ? t(avisoLimite || 'usage.limitReached')
            : t('usage.nearLimit')}
        </p>
      )}
    </div>
  );
}

function FeatureCard({ icon, name, included }) {
  return (
    <div className={`feature-card ${included ? 'included' : 'excluded'}`}>
      <span className="feature-icon">{icon}</span>
      <span className="feature-name">{name}</span>
      {included && <span className="feature-check">✓</span>}
    </div>
  );
}
